import { useCallback, useEffect, useState } from 'react';
import { Confirm, toast, type Destructive } from '@drbell/ui';
import {
  api, ApiError,
  type MedicationAdherence, type PatientSummary, type ReportResponse, type ReportShare,
} from '../api.ts';

/**
 * What to show the doctor — RX-CARE-09.
 *
 * The question a GP actually asks is not "what is the adherence percentage".
 * It is "is she taking it, and if not, which one and when". So the per-part-
 * of-day breakdown is given as much room as the headline number: misses that
 * cluster at bedtime are a different conversation from misses scattered at
 * random, and the pattern is usually the whole story.
 *
 * The share link is the point. A doctor has no DrBell account and will not
 * make one during a ten-minute appointment, so `/r/:token` serves this to
 * anybody holding the link — which is exactly why it expires by default, why
 * the list says how many times each link has been opened, and why revoking is
 * one press.
 */
const RANGES = [30, 90, 180] as const;

const PARTS: Array<['morning' | 'afternoon' | 'evening' | 'night', string]> = [
  ['morning', 'Morning'],
  ['afternoon', 'Afternoon'],
  ['evening', 'Evening'],
  ['night', 'Night'],
];

/**
 * A date on the PATIENT's calendar — B2C audit. The report printed dates in
 * the caregiver's zone next to times in the patient's, so a daughter in
 * London saw her mother's 11 pm miss on Chennai's next day stamped with the
 * wrong date, and the period's first and last days shifted by one.
 */
function dayOf(ms: number, timeZone: string): string {
  return new Date(ms).toLocaleDateString(undefined, { timeZone, day: 'numeric', month: 'short', year: 'numeric' });
}

function percent(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}

export function Report({
  patient, onBack, onStatement,
}: {
  patient: PatientSummary;
  onBack: () => void;
  /** Everything that happened, line by line, beside the numbers. */
  onStatement: () => void;
}) {
  const [days, setDays] = useState<number>(90);
  const [data, setData] = useState<ReportResponse | null>(null);
  const [shares, setShares] = useState<ReportShare[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [pending, setPending] = useState<Destructive | null>(null);
  /*
   * Whether this person may share at all — defect 127.
   *
   * Sharing and revoking both need `grant.manage`. Offering the buttons to a
   * viewer only produced a refusal after the click, so ask first and show
   * only what this person can actually do. False until known, so a slow
   * answer never flashes a button that then disappears.
   */
  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    void api
      .get<{ access: { capabilities: string[] } }>(`/api/patients/${patient.id}`)
      .then((r) => setCanShare(r.access.capabilities.includes('grant.manage')))
      .catch(() => setCanShare(false));
  }, [patient.id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, s] = await Promise.all([
        api.get<ReportResponse>(`/api/patients/${patient.id}/report?days=${days}`),
        api
          .get<{ shares: ReportShare[] }>(`/api/patients/${patient.id}/report/shares`)
          .catch(() => ({ shares: [] })),
      ]);
      setData(r);
      setShares(s.shares);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [patient.id, days]);

  useEffect(() => { void load(); }, [load]);

  if (loading && !data) return <p className="text-(--muted)">Loading…</p>;
  if (error) {
    return (
      <div className="space-y-3">
        <button onClick={onBack} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm">
          ← Back
        </button>
        <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-500">
          {error}
        </p>
      </div>
    );
  }
  if (!data) return null;

  const live = shares.filter((s) => !s.revokedAt && s.expiresAt > Date.now());

  const downloadRecord = async () => {
    try {
      const to = Date.now();
      const record = await api.get<unknown>(`/api/patients/${patient.id}/record?from=${to - days * 86_400_000}&to=${to}`);
      const blob = new Blob([JSON.stringify(record, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `drbell-record-${patient.displayName.replace(/[^\w-]+/g, '-')}-${new Date(to).toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <button onClick={onBack} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)">
          ← Back
        </button>
        <div className="flex gap-2">
          {RANGES.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              aria-pressed={days === d}
              className={`min-h-11 rounded-lg border px-3 text-sm ${
                days === d
                  ? 'border-(--accent) bg-(--accent-soft) text-(--accent)'
                  : 'border-(--line) hover:border-(--accent)'
              }`}
            >
              {d} days
            </button>
          ))}
        </div>
        {/*
          Everything that happened over the period, in one file (RX-REM-16):
          every dose with its time zone and how it was answered, every alert
          and its outcome, time-zone changes, whether the phone could ring,
          and -- for whoever may manage the record -- what was done in the app.
          For the person, the family, or a dispute.
        */}
        <button
          onClick={onStatement}
          className="ml-auto min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
        >
          Statement
        </button>
        <button
          onClick={() => void downloadRecord()}
          className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
        >
          Download full record
        </button>
        <button
          onClick={() => window.print()}
          className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
        >
          Print
        </button>
      </div>

      <p className="rounded-lg border border-(--line) bg-(--panel) px-3 py-2 text-xs text-(--muted)">
        This report shows what was reported in DrBell — a press of Taken by the person or a caregiver —
        and when. DrBell cannot see whether a medicine was actually taken. It is not proof that a medicine
        was taken, not a medical record, and not evidence of diagnosis, treatment or cure. Share it with the
        doctor as a record of reminders, and let them decide what it means.
      </p>

      {/*
        Said before any number is read, not after.
        
        A tier that shortens the window and a query that hit its row cap both
        make the figures below an undercount, and a doctor reading an
        undercount as a fact is the specific harm worth preventing.
      */}
      {data.clampedByTier && (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm">
          You asked for {data.requestedDays} days. This plan keeps{' '}
          {data.days}, so that is what is shown.
        </p>
      )}
      {data.truncated && (
        <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm">
          There is more history than one report can hold, so these totals are
          incomplete. Choose a shorter period for accurate figures.
        </p>
      )}

      <div className="rounded-xl border border-(--line) bg-(--panel) p-4">
        <h2 className="text-lg font-semibold">{data.patientName}</h2>
        <p className="text-sm text-(--muted)">
          {dayOf(data.report.from, patient.timezone)} –{' '}
          {dayOf(data.report.to, patient.timezone)} ·{' '}
          {data.report.timeZone.replace(/_/g, ' ')}
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Taken" value={percent(data.report.overallRate)} />
          <Stat label="Doses recorded" value={String(data.report.overall.resolved)} />
          <Stat label="Missed" value={String(data.report.overall.missed)} bad={data.report.overall.missed > 0} />
          <Stat label="Days with none missed" value={`${data.report.perfectDays} of ${data.report.daysCovered}`} />
        </dl>
      </div>

      {data.report.medications.map((m) => <Medication key={m.medicationId} med={m} timeZone={patient.timezone} />)}

      {data.report.medications.length === 0 && (
        <p className="rounded-xl border border-dashed border-(--line) p-8 text-center text-sm text-(--muted)">
          Nothing was recorded in this period.
        </p>
      )}

      <div className="rounded-xl border border-(--line) p-4 print:hidden">
        <h3 className="font-medium">Share with a doctor</h3>
        <p className="mt-1 text-sm text-(--muted)">
          A link anybody can open without an DrBell account — so it expires,
          and you can see how often it has been used.
        </p>

        {live.length > 0 && (
          <ul className="mt-3 space-y-2">
            {live.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-(--line) bg-(--panel) p-3 text-sm">
                <div className="min-w-0">
                  <div>{s.note || 'Shared link'}</div>
                  <div className="text-xs text-(--muted)">
                    Expires {new Date(s.expiresAt).toLocaleDateString()} ·{' '}
                    {s.viewCount === 0
                      ? 'not opened yet'
                      : `opened ${s.viewCount} time${s.viewCount === 1 ? '' : 's'}`}
                  </div>
                </div>
                {canShare && <button
                  onClick={() =>
                    setPending({
                      title: 'Revoke this link?',
                      body: 'Anybody holding it stops being able to open it, immediately.',
                      reassurance: 'Nothing is deleted. You can share a new link at any time.',
                      confirmLabel: 'Revoke it',
                      onConfirm: async () => {
                        await api.del(`/api/patients/${patient.id}/report/shares/${s.id}`);
                        toast.success('Link revoked');
                        await load();
                      },
                    })
                  }
                  className="ml-auto min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-red-500"
                >
                  Revoke
                </button>}
              </li>
            ))}
          </ul>
        )}

        {canShare && <button
          disabled={sharing}
          onClick={async () => {
            setSharing(true);
            try {
              const r = await api.post<{ token: string; url: string; expiresAt: number }>(
                `/api/patients/${patient.id}/report/share`,
                { days, expiresInDays: 14 },
              );
              /*
               * The server's URL, used as given.
               *
               * It is absolute, because only the API knows which origin
               * serves `/r/:token` — prepending this app's origin sent
               * doctors to a path the caregiver app does not route.
               */
              const full = r.url;
              /*
               * Copied, and also shown. A clipboard write can be refused —
               * an insecure origin, a browser that requires a user gesture
               * closer to the call — and a link that silently did not copy is
               * one somebody pastes an old value of into a message to a
               * doctor.
               */
              try {
                await navigator.clipboard.writeText(full);
                toast.success('Link copied', `Works for 14 days. ${full}`);
              } catch {
                toast.info('Link created — copy it from here', full);
              }
              await load();
            } catch (e) {
              toast.error(
                'Could not create a link',
                e instanceof ApiError ? e.message : String(e),
              );
            } finally {
              setSharing(false);
            }
          }}
          className="mt-3 min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
        >
          {sharing ? 'Creating…' : `Create a link to the last ${days} days`}
        </button>}
      </div>

      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </section>
  );
}

function Stat({ label, value, bad }: { label: string; value: string; bad?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-(--muted)">{label}</dt>
      <dd className={`text-2xl font-semibold tabular-nums ${bad ? 'text-red-600' : ''}`}>
        {value}
      </dd>
    </div>
  );
}

function Medication({ med, timeZone }: { med: MedicationAdherence; timeZone: string }) {
  const worst = PARTS.map(([key]) => med.byPartOfDay[key])
    .reduce((a, b) => (b.missed > a.missed ? b : a), med.byPartOfDay.morning);

  return (
    <div className="rounded-xl border border-(--line) p-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h3 className="font-medium">{med.medicationName}</h3>
        <span className="text-2xl font-semibold tabular-nums">{percent(med.rate)}</span>
        <span className="text-sm text-(--muted)">
          {med.counts.taken} taken · {med.counts.missed} missed
          {med.counts.refused > 0 && ` · ${med.counts.refused} refused`}
        </span>
      </div>

      {/*
        Where the misses fall, given equal weight to the headline.
        
        "82%" tells a doctor almost nothing. "Every miss is the night dose"
        tells them to ask whether she is asleep by then, and that is the
        conversation the report exists to start.
      */}
      <div className="mt-3 grid grid-cols-4 gap-2">
        {PARTS.map(([key, label]) => {
          const c = med.byPartOfDay[key];
          const total = c.resolved;
          return (
            <div key={key} className="rounded-lg border border-(--line) p-2 text-center">
              <div className="text-xs text-(--muted)">{label}</div>
              <div className={`text-sm font-medium tabular-nums ${c.missed > 0 ? 'text-red-600' : ''}`}>
                {total === 0 ? '—' : `${c.taken}/${total}`}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-2 text-xs text-(--muted)">
        Longest run without a miss: {med.longestStreak}
        {med.currentStreak > 0 && ` · currently ${med.currentStreak}`}
        {med.takenAfterSnooze > 0 &&
          ` · taken only after putting it off ${med.takenAfterSnooze} time${med.takenAfterSnooze === 1 ? '' : 's'}`}
      </p>

      {med.recentMisses.length > 0 && (
        <p className="mt-1 text-xs text-(--muted)">
          Recent misses:{' '}
          {med.recentMisses
            .slice(0, 5)
            .map((m) => `${dayOf(m.dueAt, timeZone)} ${m.localTime}`)
            .join(', ')}
        </p>
      )}
    </div>
  );
}
